import { type System, type World } from '@lastolivegames/becsy';

import {
  getSystemExecutionOrder,
  getComponentTypes,
  getDispatcher,
} from './becsy-internal-data-access';
import {
  type BuildCallback,
  ComponentAccessGraph,
  type AccessGraph,
  type AnyComponent,
} from './component-access-graph';

export { getSystemExecutionOrder, getComponentTypes, getDispatcher };

export type BecsyAnalyzer = {
  attachWorld: (world: World) => void;
  attachQueryBuilder: (system: System, buildCallback: BuildCallback) => BuildCallback;
  getSystemExecutionOrder: () => System[];
  getComponentTypes: () => AnyComponent[];
  getAccessGraph: () => AccessGraph;
  getStats: () => World['stats'] | undefined;
};

export class CanvasBecsyAnalyzer implements BecsyAnalyzer {
  private world: World | undefined;
  private componentAccessGraph = new ComponentAccessGraph();

  private getWorld(): World {
    const world = this.world;
    if (!world) {
      throw new Error(`World cannot be accessed before it is attached`);
    }
    return world;
  }

  //
  // setup
  //

  public attachWorld(world: World) {
    this.world = world;
    this.componentAccessGraph.attachWorld(world);
  }

  public attachQueryBuilder(system: System, buildCallback: BuildCallback): BuildCallback {
    return this.componentAccessGraph.attachQueryBuilder(system, buildCallback);
  }

  //
  // output
  //

  public getSystemExecutionOrder(): System[] {
    return getSystemExecutionOrder(this.getWorld());
  }

  public getComponentTypes(): AnyComponent[] {
    return getComponentTypes(this.getWorld());
  }

  public getAccessGraph(): AccessGraph {
    return this.componentAccessGraph.getAccessGraph();
  }

  public getStats(): World['stats'] {
    return this.getWorld().stats;
  }
}

export class NoopBecsyAnalyzer implements BecsyAnalyzer {
  attachWorld = () => {};
  attachQueryBuilder = (_system: System, buildCallback: BuildCallback): BuildCallback =>
    buildCallback;
  getSystemExecutionOrder = () => [];
  getComponentTypes = () => [];
  getAccessGraph = () => new Map();
  getStats = () => undefined;
}

export const noopAnalyzer = new NoopBecsyAnalyzer();
